import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ideasService } from '../../services/ideas';
import { Idea } from '../../types/idea-machine';
import { useDarkMode } from '../../context/DarkModeContext';
import { Loader2, Flame, MessageSquare, Clock, Trophy, Filter, ChevronDown, Send } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';

type SortOption = 'trending' | 'newest' | 'top';

export function IdeaFeedPage() {
  const { userEmail } = useAuth();
  const { isDarkMode } = useDarkMode();
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('trending');
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchIdeas();
  }, []);

  const fetchIdeas = async () => {
    setLoading(true);
    try {
      const allIdeas = await ideasService.getAllIdeas(100);
      setIdeas(allIdeas);
    } catch (error) {
      console.error('Failed to fetch ideas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpvote = async (e: React.MouseEvent, idea: Idea) => {
    e.stopPropagation();
    if (!userEmail) return;

    // Optimistic update
    const isUpvoted = idea.upvotedBy?.includes(userEmail);
    const newUpvotes = (idea.upvotes || 0) + (isUpvoted ? -1 : 1);
    const newUpvotedBy = isUpvoted
      ? idea.upvotedBy?.filter(email => email !== userEmail)
      : [...(idea.upvotedBy || []), userEmail];

    // Update ideas list
    setIdeas(prev => prev.map(i => 
      i.id === idea.id 
        ? { ...i, upvotes: newUpvotes, upvotedBy: newUpvotedBy } 
        : i
    ));

    // Update selected idea if it's the one being upvoted
    if (selectedIdea && selectedIdea.id === idea.id) {
      setSelectedIdea(prev => prev ? { ...prev, upvotes: newUpvotes, upvotedBy: newUpvotedBy } : null);
    }

    try {
      await ideasService.toggleUpvote(idea.id, userEmail);
    } catch (error) {
      console.error('Failed to toggle upvote:', error);
      // Revert on error
      fetchIdeas();
    }
  };

  const handleCommentSubmit = async (ideaId: string) => {
    if (!userEmail || !commentText.trim()) return;

    setSubmittingComment(true);
    try {
      await ideasService.addComment(ideaId, userEmail, commentText);
      setCommentText('');
      
      // Refresh ideas to get the new comment
      const updatedIdeas = await ideasService.getAllIdeas(100);
      setIdeas(updatedIdeas);
      
      // Update the selected idea if it's open
      if (selectedIdea && selectedIdea.id === ideaId) {
        const updatedSelectedIdea = updatedIdeas.find(i => i.id === ideaId);
        if (updatedSelectedIdea) {
          setSelectedIdea(updatedSelectedIdea);
        }
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  // Define color palette with both Tailwind classes and HSLA values
  const colorPalette = [
    { name: 'red', classes: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800', hue: 0 },
    { name: 'orange', classes: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800', hue: 30 },
    { name: 'amber', classes: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800', hue: 45 },
    { name: 'yellow', classes: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800', hue: 60 },
    { name: 'lime', classes: 'bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-300 border-lime-200 dark:border-lime-800', hue: 84 },
    { name: 'green', classes: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800', hue: 142 },
    { name: 'emerald', classes: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800', hue: 160 },
    { name: 'teal', classes: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 border-teal-200 dark:border-teal-800', hue: 173 },
    { name: 'cyan', classes: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800', hue: 189 },
    { name: 'sky', classes: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 border-sky-200 dark:border-sky-800', hue: 204 },
    { name: 'blue', classes: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800', hue: 217 },
    { name: 'indigo', classes: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800', hue: 244 },
    { name: 'violet', classes: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 border-violet-200 dark:border-violet-800', hue: 262 },
    { name: 'purple', classes: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800', hue: 271 },
    { name: 'fuchsia', classes: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300 border-fuchsia-200 dark:border-fuchsia-800', hue: 292 },
    { name: 'pink', classes: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300 border-pink-200 dark:border-pink-800', hue: 330 },
    { name: 'rose', classes: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300 border-rose-200 dark:border-rose-800', hue: 343 },
  ];

  const getColorData = (category: string) => {
    let hash = 0;
    for (let i = 0; i < category.length; i++) {
      hash = category.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colorPalette[Math.abs(hash) % colorPalette.length];
  };

  const getCategoryColor = (category: string) => {
    return getColorData(category).classes;
  };

  const getCardBackgroundStyle = (category: string) => {
    const { hue } = getColorData(category);
    const saturation = isDarkMode ? 30 : 70;
    const lightness = isDarkMode ? 15 : 97;
    
    // Generate a pseudo-random position for the gradient based on the category name
    // so it's consistent for the category but varies slightly if we wanted (or keep static)
    let hash = 0;
    for (let i = 0; i < category.length; i++) {
      hash = category.charCodeAt(i) + ((hash << 5) - hash);
    }

    return {
      backgroundColor: `hsla(${hue}, ${saturation}%, ${lightness}%, ${isDarkMode ? 0.4 : 0.5})`,
      backgroundImage: `
        radial-gradient(circle at ${Math.abs(hash * 2) % 100}% ${Math.abs(hash * 3) % 100}%, 
        hsla(${hue}, ${saturation}%, ${isDarkMode ? 25 : 90}%, 0.5) 0%, 
        transparent 50%)
      `
    };
  };

  const sortedIdeas = useMemo(() => {
    return [...ideas].sort((a, b) => {
      if (sortBy === 'newest') {
        return b.timestamp.getTime() - a.timestamp.getTime();
      } else if (sortBy === 'top') {
        return (b.upvotes || 0) - (a.upvotes || 0);
      } else {
        // Trending: Mix of recency and upvotes (simple algorithm)
        const scoreA = (a.upvotes || 0) * 10 + a.timestamp.getTime() / 1000000000;
        const scoreB = (b.upvotes || 0) * 10 + b.timestamp.getTime() / 1000000000;
        return scoreB - scoreA;
      }
    });
  }, [ideas, sortBy]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className={`h-full overflow-y-auto p-6 ${isDarkMode ? 'bg-gray-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight font-display mb-2">Idea Feed</h1>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Discover and support the best ideas from the team.
            </p>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Filter className="h-4 w-4" />
                {sortBy === 'trending' ? 'Trending' : sortBy === 'newest' ? 'Newest' : 'Top Rated'}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSortBy('trending')}>
                <Flame className="h-4 w-4 mr-2" /> Trending
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy('newest')}>
                <Clock className="h-4 w-4 mr-2" /> Newest
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy('top')}>
                <Trophy className="h-4 w-4 mr-2" /> Top Rated
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Masonry Grid */}
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
          {sortedIdeas.map((idea) => (
            <div 
              key={idea.id} 
              onClick={() => setSelectedIdea(idea)}
              style={getCardBackgroundStyle(idea.seedCategory || 'General')}
              className={`break-inside-avoid rounded-xl border p-5 transition-all duration-300 hover:shadow-xl cursor-pointer group backdrop-blur-sm ${
                isDarkMode 
                  ? 'border-gray-800 hover:border-indigo-500/30' 
                  : 'border-gray-200 hover:border-indigo-300'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className={`${getCategoryColor(idea.seedCategory || 'General')} border`}>
                      {idea.seedCategory || 'General'}
                    </Badge>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {formatDistanceToNow(idea.timestamp, { addSuffix: true })}
                    </span>
                  </div>
                  <h3 className="font-medium text-sm text-gray-500 dark:text-gray-400 line-clamp-1 group-hover:text-indigo-500 transition-colors">
                    {idea.seedPrompt}
                  </h3>
                </div>
              </div>

              {/* Content Preview */}
              <div className="space-y-2 mb-4">
                {idea.content.slice(0, 3).map((text, idx) => (
                  <p key={idx} className="font-semibold text-lg leading-snug">
                    {text}
                  </p>
                ))}
                {idea.content.length > 3 && (
                  <p className="text-sm text-gray-500 italic">
                    + {idea.content.length - 3} more ideas...
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-4">
                  <div className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                      idea.upvotedBy?.includes(userEmail || '')
                        ? 'text-orange-500'
                        : 'text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300'
                    }`}
                  >
                    <Flame className={`h-4 w-4 ${idea.upvotedBy?.includes(userEmail || '') ? 'fill-orange-500' : ''}`} />
                    <span>{idea.upvotes || 0}</span>
                  </div>

                  <div className="flex items-center gap-1.5 text-sm font-medium text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">
                    <MessageSquare className="h-4 w-4" />
                    <span>{idea.comments?.length || 0}</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Avatar className="h-5 w-5">
                    <AvatarFallback className="text-[9px] bg-gray-100 dark:bg-gray-800">
                      {idea.userEmail.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span>{idea.userEmail.split('@')[0]}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Idea Detail Modal */}
        <Dialog open={!!selectedIdea} onOpenChange={(open) => !open && setSelectedIdea(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
            {selectedIdea && (
              <>
                <DialogHeader className="p-6 pb-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className={`${getCategoryColor(selectedIdea.seedCategory || 'General')} border`}>
                      {selectedIdea.seedCategory || 'General'}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {formatDistanceToNow(selectedIdea.timestamp, { addSuffix: true })}
                    </span>
                  </div>
                  <DialogTitle className="text-xl font-light text-gray-600 dark:text-gray-300">
                    {selectedIdea.seedPrompt}
                  </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-6 min-h-0">
                  <div className="space-y-8">
                    {/* Main Content */}
                    <div className="space-y-4">
                      {selectedIdea.content.map((text, idx) => (
                        <div key={idx} className="flex gap-4 group">
                          <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 text-sm font-bold opacity-50 group-hover:opacity-100 transition-opacity">
                            {idx + 1}
                          </span>
                          <p className="text-xl font-medium leading-relaxed text-gray-900 dark:text-gray-100">
                            {text}
                          </p>
                        </div>
                      ))}
                    </div>

                    <Separator />

                    {/* Interaction Area */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Button
                          size="lg"
                          variant={selectedIdea.upvotedBy?.includes(userEmail || '') ? "default" : "outline"}
                          className={`gap-2 transition-all duration-300 ${
                            selectedIdea.upvotedBy?.includes(userEmail || '') 
                              ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/20 scale-105' 
                              : 'hover:border-orange-500 hover:text-orange-500'
                          }`}
                          onClick={(e) => handleUpvote(e, selectedIdea)}
                        >
                          <Flame className={`h-5 w-5 ${selectedIdea.upvotedBy?.includes(userEmail || '') ? 'fill-current animate-pulse' : ''}`} />
                          <span className="font-bold">{selectedIdea.upvotes || 0} Sparks</span>
                        </Button>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="h-4 w-4" />
                        <span>{selectedIdea.timeSpent}m spent thinking</span>
                      </div>
                    </div>

                    {/* Comments Section */}
                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-6 space-y-6">
                      <h3 className="font-semibold flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        Discussion ({selectedIdea.comments?.length || 0})
                      </h3>

                      <div className="space-y-4">
                        {selectedIdea.comments?.map((comment) => (
                          <div key={comment.id} className="flex gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                                {comment.userEmail.substring(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {comment.userEmail.split('@')[0]}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatDistanceToNow(comment.timestamp, { addSuffix: true })}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                {comment.text}
                              </p>
                            </div>
                          </div>
                        ))}
                        
                        {(!selectedIdea.comments || selectedIdea.comments.length === 0) && (
                          <div className="text-center py-8 text-gray-400">
                            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-20" />
                            <p className="text-sm">No comments yet. Start the conversation!</p>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-3 pt-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400">
                            {userEmail?.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 flex gap-2">
                          <input
                            type="text"
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            placeholder="Add a comment..."
                            className={`flex-1 text-sm rounded-lg px-4 py-2 border transition-all duration-200 ${
                              isDarkMode 
                                ? 'bg-gray-800 border-gray-700 focus:border-indigo-500 focus:bg-gray-800' 
                                : 'bg-white border-gray-200 focus:border-indigo-500 focus:bg-white'
                            } outline-none focus:ring-2 focus:ring-indigo-500/20`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleCommentSubmit(selectedIdea.id);
                            }}
                          />
                          <Button 
                            size="icon"
                            className="shrink-0"
                            onClick={() => handleCommentSubmit(selectedIdea.id)}
                            disabled={submittingComment || !commentText.trim()}
                          >
                            {submittingComment ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Send className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
